import type { Locale, PageKind } from "../content/types";

const LOCALE_STORAGE_KEY = "luxoria_lang";

function normalizeLanguage(language: string) {
  return language.trim().toLowerCase().split("-")[0];
}

export function getSavedLocale(): Locale | null {
  try {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (saved === "zh" || saved === "en") return saved;
  } catch {
    /* ignore unavailable storage */
  }
  return null;
}

export function saveLocale(locale: Locale) {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* ignore unavailable storage */
  }
}

export function getBrowserLocale(
  languages: readonly string[] = typeof navigator === "undefined" ? [] : navigator.languages
): Locale {
  for (const language of languages) {
    if (normalizeLanguage(language) === "zh") return "zh";
  }
  return "en";
}

/** Resolve the locale used for the `/` entry route: saved preference, then browser, default en. */
export function resolveEntryLocale(): Locale {
  return getSavedLocale() ?? getBrowserLocale();
}

const base = import.meta.env.BASE_URL || "/";

/** Build a public-asset URL honouring the Vite base path, e.g. "images/Icon.png". */
export function assetPath(path: string) {
  return `${base}${path.replace(/^\//, "")}`;
}

/** Build an in-app path honouring the Vite base path. */
export function routePath(path: string) {
  if (base === "/") return path;
  const cleanBase = base.replace(/\/$/, "");
  return `${cleanBase}${path}`;
}

const suffixByKind: Record<PageKind, string> = {
  landing: "",
  privacy: "/privacy",
  terms: "/terms",
  support: "/support"
};

/** Path for a given locale + page kind. English is unprefixed (e.g. "/privacy"), Chinese keeps "/zh". */
export function localePath(locale: Locale, kind: PageKind = "landing") {
  const suffix = suffixByKind[kind];
  if (locale === "en") return suffix === "" ? "/" : suffix;
  return `/${locale}${suffix}`;
}

/** Href (base-path aware) for a locale + page kind. */
export function localeHref(locale: Locale, kind: PageKind = "landing") {
  return routePath(localePath(locale, kind));
}

/** The opposite locale of the current one. */
export function otherLocale(locale: Locale): Locale {
  return locale === "en" ? "zh" : "en";
}
